import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { injectMutation, injectQuery, injectQueryClient } from '@tanstack/angular-query-experimental';
import { filter, lastValueFrom } from 'rxjs';

import { ActionTakenFeedbackData, ConciergeApiService, ConciergeItemsResponse, CreateConciergeItemData, TicketCommentData } from './concierge-api.service';
import { isCommentFeedbackEligible } from 'src/shared/util/comment-feedback';
import { SseService } from './sse.service';
import { Auth } from 'src/providers/auth/auth';
import { SseChatUpdatedPayload } from 'src/models/concierge.model';

export const conciergeKeys = {
  all: ['concierge'] as const,
  items: (filters?: string) => [...conciergeKeys.all, 'items', filters ?? ''] as const,
  item: (id: number) => [...conciergeKeys.all, 'item', id] as const,
  sessions: () => [...conciergeKeys.all, 'sessions'] as const,
  rules: () => [...conciergeKeys.all, 'rules'] as const,
  suggestions: () => [...conciergeKeys.all, 'suggestions'] as const,
  upsells: (page: number, perPage: number) => [...conciergeKeys.all, 'upsells', page, perPage] as const,
};

export interface PaginationMetadata {
  total_records: number;
  total_pages: number;
  per_page: number;
  current_page: number;
  has_previous_page: boolean;
  has_next_page: boolean;
}

export interface ChatMessage {
  id: number;
  role: 'ai' | 'user';
  content: string;
  timestamp?: string;
  senderName?: string;
  attachments?: string[];
  userType?: string;
  isFeedbackEligible?: boolean;
}

export interface ChatSession {
  id: number;
  title: string;
  preview: string;
  timestamp: Date;
  messages: ChatMessage[];
  supportTicketId?: number;
}

@Injectable({ providedIn: 'root' })
export class ConciergeQueriesService {
  private readonly api = inject(ConciergeApiService);
  private readonly auth = inject(Auth);
  private readonly sseService = inject(SseService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly queryClient = injectQueryClient();

  // Avoids a double refresh when sending a comment by skipping the SSE echo.
  private skipNextCommentEcho = false;

  readonly selectedSessionId = signal<number | null>(null);
  readonly isPollingEnabled = signal(false);
  readonly isSseConnected = computed(() => this.sseService.connectionState() === 'connected');
  // Updated via SSE when the AI agent starts/stops processing, used to hide the typing indicator.
  readonly sseAgentStatus = signal<{ status: string; timestamp: number } | null>(null);
  // Bumped each time a non-echo comment_added SSE event fires for the selected session.
  readonly aiCommentReceivedAt = signal<number>(0);

  constructor() {
    // Sync recent_comments from item detail query to allLoadedMessages signal
    effect(() => {
      const item = this.selectedItemDetailQuery.data();
      if (!item) { return; }

      const recentComments = item?.recent_comments;
      if (recentComments && recentComments.length > 0) {
        const newMessages = this.mapCommentsToMessages(recentComments);

        if (this.messagesPage() === 1) {
          this.allLoadedMessages.set(newMessages);
        } else {
          // Paginated: append only genuinely new messages
          const currentMessages = this.allLoadedMessages();
          const existingIds = new Set(currentMessages.map(m => m.id));
          const newOnly = newMessages.filter(m => !existingIds.has(m.id));
          if (newOnly.length > 0) {
            this.allLoadedMessages.update(msgs => [...msgs, ...newOnly]);
          }
        }
        this.hasMoreMessages.set(recentComments.length >= this.messagesPerPage());
      } else if (this.messagesPage() === 1 && item?.components?.length > 0) {
        const componentMessages = this.mapComponentsToMessages(item.components);
        if (componentMessages.length > 0) {
          this.allLoadedMessages.set(componentMessages);
        }
        this.hasMoreMessages.set(false);
      }
    });

    this.subscribeToChatAgentStatus();
    this.subscribeToChatComments();
  }

  private readonly chatUpdates$ = this.sseService
    .on<SseChatUpdatedPayload>('concierge_chat.updated')
    .pipe(takeUntilDestroyed(this.destroyRef));

  private subscribeToChatAgentStatus(): void {
    this.chatUpdates$.pipe(
      filter(event => event.data?.type === 'agent_status_updated')
    ).subscribe(event => {
      const { concierge_item_id, data: payload } = event.data;
      if (concierge_item_id === this.selectedSessionId()) {
        this.sseAgentStatus.set({ status: payload?.status ?? '', timestamp: Date.now() });
      }
    });
  }

  private subscribeToChatComments(): void {
    this.chatUpdates$.pipe(
      filter(event => event.data?.type === 'comment_added')
    ).subscribe(event => {
      const { concierge_item_id } = event.data;

      if (this.skipNextCommentEcho) {
        this.skipNextCommentEcho = false;
        return;
      }

      if (concierge_item_id === this.selectedSessionId()) {
        this.aiCommentReceivedAt.set(Date.now());
        this.invalidateSelectedItem();
      } else {
        this.invalidateSessions();
      }
    });
  }

  // Messages pagination state
  readonly messagesPage = signal(1);
  readonly messagesPerPage = signal(50);
  readonly hasMoreMessages = signal(false);
  readonly isLoadingMoreMessages = signal(false);
  readonly allLoadedMessages = signal<ChatMessage[]>([]);

  // Upsells pagination state
  readonly upsellsPage = signal(1);
  readonly upsellsPerPage = signal(50);

  readonly upsellsQuery = injectQuery(() => ({
    queryKey: conciergeKeys.upsells(this.upsellsPage(), this.upsellsPerPage()),
    queryFn: () => this.fetchUpsells(this.upsellsPage(), this.upsellsPerPage()),
    enabled: this.auth.authenticated(),
  }));

  readonly upsells = computed(() => this.upsellsQuery.data()?.body ?? []);
  readonly totalUpsells = computed(() => this.upsellsQuery.data()?.totalRecords ?? '0');
  readonly isLoadingUpsells = computed(() => this.upsellsQuery.isPending());
  readonly upsellsError = computed(() => this.upsellsQuery.error());

  readonly sessionsQuery = injectQuery(() => ({
    queryKey: conciergeKeys.sessions(),
    queryFn: () => this.fetchSessions(),
    enabled: this.auth.authenticated(),
  }));

  readonly sessions = computed(() => this.sessionsQuery.data() ?? []);
  readonly isLoadingSessions = computed(() => this.sessionsQuery.isPending());
  readonly sessionsError = computed(() => this.sessionsQuery.error());

  readonly selectedItemDetailQuery = injectQuery(() => ({
    queryKey: conciergeKeys.item(this.selectedSessionId() ?? 0),
    queryFn: async () => {
      const sessionId = this.selectedSessionId();
      if (!sessionId) { return null; }
      return lastValueFrom(this.api.getConciergeItemById(sessionId));
    },
    enabled: this.selectedSessionId() !== null,
    refetchInterval: this.isPollingEnabled() && !this.isSseConnected() ? 5000 : false,
    refetchIntervalInBackground: false,
  }));

  readonly selectedMessages = computed(() => this.allLoadedMessages());
  readonly isLoadingMessages = computed(() => {
    const isFetching = this.selectedItemDetailQuery.isFetching();
    const hasData = this.allLoadedMessages().length > 0;
    // Show loading on initial fetch (no data), hide during polling refetches (has data)
    return isFetching && !hasData;
  });
  readonly messagesError = computed(() => this.selectedItemDetailQuery.error());

  readonly selectedItemStatus = computed(() => {
    const item = this.selectedItemDetailQuery.data();
    return item?.components?.[0]?.value?.status ?? null;
  });

  readonly isEscalated = computed(() =>
    this.selectedSessionId() !== null && this.selectedItemStatus() === 'waiting_on_tidy'
  );

  // The backend agent only answers while the ticket is handled_by_agent. Any other status means
  // our team has it: escalated, already answered by a concierge (waiting_on_customer), or closed.
  readonly isHandedOff = computed(() => {
    const status = this.selectedItemStatus();
    return this.selectedSessionId() !== null && status !== null && status !== 'handled_by_agent';
  });

  // True only when the currently-open thread is itself a dispute concierge item.
  // The dispute banner keys off this so it never fires in unrelated support
  // threads that merely mention the word "dispute".
  readonly isDisputeThread = computed(() =>
    this.selectedItemDetailQuery.data()?.type?.startsWith('dispute.') ?? false
  );

  readonly isAgentProcessing = computed(() => {
    const item = this.selectedItemDetailQuery.data();
    return item?.agent_info?.processing === true;
  });

  readonly isAwaitingResponse = computed(() => {
    const status = this.selectedItemStatus();
    return status !== null && status !== 'waiting_on_tidy';
  });

  readonly createItemMutation = injectMutation(() => ({
    mutationFn: (data: CreateConciergeItemData) => lastValueFrom(this.api.createConciergeItem(data)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: conciergeKeys.sessions() });
    },
  }));

  readonly addCommentMutation = injectMutation(() => ({
    mutationFn: (data: TicketCommentData) => {
      this.skipNextCommentEcho = true;
      return lastValueFrom(this.api.addTicketComment(data));
    },
  }));

  readonly updateSubjectMutation = injectMutation(() => ({
    mutationFn: ({ itemId, subject }: { itemId: number; subject: string }) =>
      lastValueFrom(this.api.updateConciergeItemSubject(itemId, subject)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({ queryKey: conciergeKeys.sessions() });
    },
  }));

  readonly closeRequestMutation = injectMutation(() => ({
    mutationFn: (itemId: number) => lastValueFrom(this.api.closeSupportRequest(itemId)),
    onSuccess: (_data, itemId) => {
      this.queryClient.invalidateQueries({ queryKey: conciergeKeys.sessions() });
      this.queryClient.removeQueries({ queryKey: conciergeKeys.item(itemId) });
    },
  }));

  readonly messageFeedback = signal<Record<number, 'positive' | 'negative'>>({});

  readonly submitFeedbackMutation = injectMutation(() => ({
    mutationFn: (data: ActionTakenFeedbackData) => lastValueFrom(this.api.submitFeedback(data)),
  }));

  setMessageFeedback(messageId: number, type: 'positive' | 'negative'): void {
    this.messageFeedback.update(current => ({ ...current, [messageId]: type }));
  }

  private async fetchSessions(): Promise<ChatSession[]> {
    const filters = '&not_concierge_item_type_key=concierge_task,midstay_cleaning,late_checkout,early_checkin,ai_agent_task&page=1&per_page=1000&is_marked_done=false';
    const response = await lastValueFrom(this.api.getConciergeItems(filters));
    return response.body.map(item => this.mapConciergeItemToSession(item));
  }

  private fetchUpsells(page: number, perPage: number): Promise<ConciergeItemsResponse> {
    const filters = `&concierge_item_type_key=early_checkin,late_checkout,midstay_cleaning&page=${page}&per_page=${perPage}&is_marked_done=false`;
    return lastValueFrom(this.api.getConciergeItems(filters));
  }

  private async fetchSessionMessages(
    sessionId: number,
    page = 1,
    perPage = 50
  ): Promise<{ messages: ChatMessage[]; metadata: PaginationMetadata }> {
    const response = await lastValueFrom(this.api.getConciergeItemComments(sessionId, page, perPage));
    const totalPages = Math.ceil(response.totalRecords / perPage);

    return {
      messages: this.mapCommentsToMessages(response.comments),
      metadata: {
        total_records: response.totalRecords,
        total_pages: totalPages,
        per_page: perPage,
        current_page: page,
        has_previous_page: page > 1,
        has_next_page: page < totalPages
      }
    };
  }

  private mapConciergeItemToSession(item: any): ChatSession {
    const componentValue = item.components?.[0]?.value;
    const isComponentArray = Array.isArray(componentValue);
    const supportTicket = isComponentArray ? null : componentValue;
    const comments = supportTicket?.comments || [];
    const lastComment = comments[comments.length - 1];

    let preview: string;
    if (lastComment?.content) {
      preview = `${lastComment.content.substring(0, 50)}${lastComment.content.length > 50 ? '...' : ''}`;
    } else if (isComponentArray && componentValue.length > 0) {
      const firstHtml = componentValue[0]?.html || '';
      const plainText = firstHtml.replace(/<[^>]*>/g, '');
      preview = `${plainText.substring(0, 50)}${plainText.length > 50 ? '...' : ''}`;
    } else {
      preview = 'No messages yet';
    }

    return {
      id: item.id,
      title: item.header_text || supportTicket?.subject || 'Support Request',
      preview,
      timestamp: new Date(supportTicket?.updated_at || item.created_at),
      messages: [],
      supportTicketId: supportTicket?.id
    };
  }

  private mapCommentsToMessages(comments: any[]): ChatMessage[] {
    // API returns newest first, but chat UI needs oldest first
    return [...comments].reverse().map(comment => ({
      id: comment.id,
      role: this.getUserRole(comment.user_type),
      content: comment.content,
      timestamp: comment.created_at,
      senderName: comment.user_first_name,
      attachments: (comment.attachments || []).filter((url: string) => url.startsWith('http')),
      userType: comment.user_type,
      isFeedbackEligible: isCommentFeedbackEligible(comment.user_type)
    }));
  }

  private mapComponentsToMessages(components: any[]): ChatMessage[] {
    const messages: ChatMessage[] = [];
    for (const component of components) {
      if (component.key === 'multiline_text' && Array.isArray(component.value)) {
        const htmlParts = component.value
          .map((v: any) => v.html?.replace(/<[^>]*>/g, '') || '')
          .filter((text: string) => !!text);
        if (htmlParts.length > 0) {
          messages.push({
            id: component.id || Date.now(),
            role: 'ai',
            content: htmlParts.join('\n\n'),
            senderName: 'TIDY',
          });
        }
      }
    }
    return messages;
  }

  private getUserRole(userType: string): 'ai' | 'user' {
    const customerTypes = ['customer', 'Customer', 'CustomerMember'];
    return customerTypes.includes(userType) ? 'user' : 'ai';
  }

  selectSession(sessionId: number | null): void {
    const previousSessionId = this.selectedSessionId();
    this.selectedSessionId.set(sessionId);
    // Reset pagination state
    this.messagesPage.set(1);
    this.allLoadedMessages.set([]);
    this.hasMoreMessages.set(false);
    this.sseAgentStatus.set(null);
    this.aiCommentReceivedAt.set(0);
    this.skipNextCommentEcho = false;
    this.messageFeedback.set({});
    // Clear cached item detail when deselecting to prevent stale escalation status
    if (sessionId === null && previousSessionId !== null) {
      this.queryClient.removeQueries({ queryKey: conciergeKeys.item(previousSessionId) });
    }
  }

  setPollingEnabled(enabled: boolean): void {
    this.isPollingEnabled.set(enabled);
  }

  invalidateSessions(): void {
    this.queryClient.invalidateQueries({ queryKey: conciergeKeys.sessions() });
  }

  invalidateSelectedItem(): void {
    const sessionId = this.selectedSessionId();
    if (sessionId) {
      this.queryClient.invalidateQueries({
        queryKey: conciergeKeys.item(sessionId)
      });
    }
  }

  getSessionById(sessionId: number): ChatSession | undefined {
    return this.sessions().find(s => s.id === sessionId);
  }

  setUpsellsPage(page: number): void {
    this.upsellsPage.set(page);
  }

  invalidateUpsells(): void {
    this.queryClient.invalidateQueries({ queryKey: conciergeKeys.upsells(this.upsellsPage(), this.upsellsPerPage()) });
  }

  refetchUpsells(): void {
    this.upsellsQuery.refetch();
  }

  async refetchSessions(): Promise<void> {
    await this.sessionsQuery.refetch();
  }

  async refetchMessages(): Promise<void> {
    const sessionId = this.selectedSessionId();
    if (sessionId) {
      await this.queryClient.refetchQueries({
        queryKey: conciergeKeys.item(sessionId)
      });
    }
  }

  async getConciergeItemById(itemId: number): Promise<any> {
    return lastValueFrom(this.api.getConciergeItemById(itemId));
  }

  async loadMoreMessages(): Promise<void> {
    if (this.isLoadingMoreMessages() || !this.hasMoreMessages()) {return;}

    this.isLoadingMoreMessages.set(true);
    try {
      const nextPage = this.messagesPage() + 1;
      const sessionId = this.selectedSessionId();
      if (!sessionId) {return;}

      const result = await this.fetchSessionMessages(sessionId, nextPage, this.messagesPerPage());

      // Prepend older messages to existing
      const current = this.allLoadedMessages();
      this.allLoadedMessages.set([...result.messages, ...current]);
      this.messagesPage.set(nextPage);
      this.hasMoreMessages.set(result.metadata.has_next_page);
    } finally {
      this.isLoadingMoreMessages.set(false);
    }
  }
}
